import React, { useState, useRef } from 'react';
import {
  Form,
  Button,
  Message,
  Progress,
  Icon,
  Label,
  Dropdown,
  Input
} from 'semantic-ui-react';
import {
  uploadFile,
  validateFileSize,
  formatFileSize,
  copyToClipboard,
  formatDate
} from '../utils/sharedFilesApi';

const FileUpload = ({ getApiHeaders, onUploadSuccess }) => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [description, setDescription] = useState('');
  const [expirationDays, setExpirationDays] = useState(30);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadResult, setUploadResult] = useState(null);
  const [error, setError] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [copySuccess, setCopySuccess] = useState(false);

  const fileInputRef = useRef(null);

  const expirationOptions = [
    { key: '1', text: '1 day', value: 1 },
    { key: '7', text: '7 days', value: 7 },
    { key: '30', text: '30 days', value: 30 },
    { key: '90', text: '90 days', value: 90 },
    { key: '180', text: '180 days', value: 180 },
    { key: '365', text: '1 year', value: 365 },
    { key: 'never', text: 'Never expires', value: null }
  ];

  const handleFileSelect = (file) => {
    if (!file) return;

    const validation = validateFileSize(file);
    if (!validation.valid) {
      setError(validation.error);
      return;
    }

    setSelectedFile(file);
    setError(null);
    setUploadResult(null);
  };

  const handleFileInputChange = (e) => {
    const file = e.target.files[0];
    handleFileSelect(file);
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      handleFileSelect(files[0]);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      setError('Please select a file to upload');
      return;
    }

    setUploading(true);
    setUploadProgress(0);
    setError(null);

    const result = await uploadFile(
      selectedFile,
      getApiHeaders,
      expirationDays,
      description,
      (progress) => setUploadProgress(progress)
    );

    setUploading(false);

    if (result.success) {
      setUploadResult(result.data);
      setSelectedFile(null);
      setDescription('');
      setExpirationDays(30);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      if (onUploadSuccess) {
        onUploadSuccess(result.data);
      }
    } else {
      setError(result.error);
    }
  };

  const handleCopyLink = async () => {
    if (!uploadResult) return;

    const success = await copyToClipboard(uploadResult.public_url);
    if (success) {
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    } else {
      setError('Failed to copy link to clipboard');
    }
  };

  const handleReset = () => {
    setUploadResult(null);
    setSelectedFile(null);
    setDescription('');
    setExpirationDays(30);
    setError(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div>
      {!uploadResult ? (
        <>
          {error && (
            <Message negative onDismiss={() => setError(null)}>
              <Message.Header>Upload Error</Message.Header>
              <p>{error}</p>
            </Message>
          )}

          <div
            style={{
              border: isDragging ? '2px dashed #2185d0' : '2px dashed #d4d4d5',
              borderRadius: '8px',
              padding: '40px 20px',
              textAlign: 'center',
              marginBottom: '20px',
              backgroundColor: isDragging ? '#f0f8ff' : '#f9f9f9',
              cursor: 'pointer',
              transition: 'all 0.3s ease'
            }}
            onDragEnter={handleDragEnter}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
          >
            <input
              ref={fileInputRef}
              type="file"
              onChange={handleFileInputChange}
              style={{ display: 'none' }}
            />

            {!selectedFile ? (
              <>
                <Icon name="cloud upload" size="huge" color="grey" />
                <h4>Drag & drop file here</h4>
                <p style={{ color: '#666' }}>or click to browse</p>
                <p style={{ color: '#999', fontSize: '0.9em' }}>
                  Maximum file size: 100MB
                </p>
              </>
            ) : (
              <>
                <Icon name="file" size="huge" color="blue" />
                <h4>{selectedFile.name}</h4>
                <p style={{ color: '#666' }}>{formatFileSize(selectedFile.size)}</p>
                <Button
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedFile(null);
                    if (fileInputRef.current) {
                      fileInputRef.current.value = '';
                    }
                  }}
                >
                  Remove
                </Button>
              </>
            )}
          </div>

          <Form>
            <Form.Field>
              <label>Expiration</label>
              <Dropdown
                placeholder="Select expiration"
                fluid
                selection
                options={expirationOptions}
                value={expirationDays}
                onChange={(e, { value }) => setExpirationDays(value)}
                disabled={uploading}
              />
            </Form.Field>

            <Form.Field>
              <label>Description (optional)</label>
              <Input
                placeholder="Brief description of the file"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                disabled={uploading}
                maxLength={200}
              />
            </Form.Field>

            {uploading && (
              <Progress
                percent={uploadProgress}
                indicating
                progress
                label={`Uploading... ${uploadProgress}%`}
              />
            )}

            <Button
              primary
              fluid
              size="large"
              onClick={handleUpload}
              disabled={!selectedFile || uploading}
              loading={uploading}
            >
              <Icon name="upload" />
              Upload File
            </Button>
          </Form>
        </>
      ) : (
        <>
          <Message success>
            <Message.Header>
              <Icon name="check circle" />
              File uploaded successfully!
            </Message.Header>
          </Message>

          <div style={{ marginTop: '20px', marginBottom: '20px' }}>
            <h4>Share Link:</h4>
            <Input
              fluid
              value={uploadResult.public_url}
              readOnly
              action={{
                color: copySuccess ? 'green' : 'blue',
                icon: copySuccess ? 'check' : 'copy',
                content: copySuccess ? 'Copied!' : 'Copy Link',
                onClick: handleCopyLink
              }}
            />
          </div>

          <div style={{ marginTop: '20px' }}>
            <Label>
              <Icon name="file" />
              {uploadResult.original_filename}
            </Label>
            <Label>
              <Icon name="database" />
              {formatFileSize(uploadResult.file_size)}
            </Label>
            {uploadResult.expires_at && (
              <Label>
                <Icon name="clock" />
                Expires: {formatDate(uploadResult.expires_at)}
              </Label>
            )}
            {!uploadResult.expires_at && (
              <Label color="green">
                <Icon name="infinity" />
                Never expires
              </Label>
            )}
          </div>

          <div style={{ marginTop: '20px' }}>
            <Button primary onClick={handleReset}>
              <Icon name="upload" />
              Upload Another File
            </Button>
          </div>
        </>
      )}
    </div>
  );
};

export default FileUpload;
